import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Linkedin, Mail } from "lucide-react";

const COL_PRODUCT = [
  { to: "/products", label: "All Compounds" },
  { to: "/lookbook", label: "Research Library" },
  { to: "/about", label: "About LuviaRx" },
  { to: "/shipping", label: "Shipping & Returns" },
  { to: "/faq", label: "FAQ" },
];

const COL_LEGAL = [
  { to: "/legal/terms", label: "Terms & Conditions" },
  { to: "/legal/privacy", label: "Privacy Policy" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/legal/shipping", label: "Shipping Policy" },
  { to: "/legal/research-use", label: "Research Use Policy" },
  { to: "/legal/disclaimer", label: "Disclaimer" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-charcoal-600 text-cream-200 mt-24">
    <div className="container-x py-16">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div
            className="mb-5 inline-flex items-baseline rounded-2xl px-5 py-3 select-none"
            style={{ background: "#2A2023" }}
          >
            <span
              className="font-serif"
              style={{ fontWeight: 600, fontSize: "44px", letterSpacing: "0.20em", color: "#FDFAF9", lineHeight: 1 }}
            >
              LUVIA
            </span>
            <span
              className="font-serif lowercase ml-[5px]"
              style={{ fontWeight: 600, fontSize: "28px", letterSpacing: "0.10em", color: "#B05580", lineHeight: 1 }}
            >
              rx
            </span>
          </div>
          <p className="text-sm text-cream-200/70 max-w-sm leading-relaxed">
            Premium research compounds intended exclusively for laboratory and scientific research purposes. Verified quality. Transparent sourcing.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <a aria-label="Instagram" href="#" className="w-9 h-9 rounded-full bg-cream-100/5 hover:bg-sage-400 transition-colors flex items-center justify-center">
              <Instagram className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <a aria-label="Twitter" href="#" className="w-9 h-9 rounded-full bg-cream-100/5 hover:bg-sage-400 transition-colors flex items-center justify-center">
              <Twitter className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <a aria-label="LinkedIn" href="#" className="w-9 h-9 rounded-full bg-cream-100/5 hover:bg-sage-400 transition-colors flex items-center justify-center">
              <Linkedin className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <a aria-label="Email" href="mailto:support@luviarx.com" className="w-9 h-9 rounded-full bg-cream-100/5 hover:bg-sage-400 transition-colors flex items-center justify-center">
              <Mail className="w-4 h-4" strokeWidth={1.6} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-cream-100 font-display font-bold text-sm tracking-[0.18em] uppercase mb-4">Explore</h4>
          <ul className="space-y-3 text-sm">
            {COL_PRODUCT.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-cream-200/70 hover:text-sage-300 transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-cream-100 font-display font-bold text-sm tracking-[0.18em] uppercase mb-4">Legal</h4>
          <ul className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm">
            {COL_LEGAL.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.to.split("/").pop()}`}
                  className="text-cream-200/70 hover:text-sage-300 transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-cream-200/60">
            <span className="block mb-1">
              <a
                href="mailto:support@luviarx.com"
                data-testid="footer-email"
                className="hover:text-cream-100 transition-colors"
              >
                ✉️ support@luviarx.com
              </a>
            </span>
            <span className="block">
              <a
                href="sms:+15612880915"
                data-testid="footer-sms"
                className="hover:text-cream-100 transition-colors"
              >
                💬 Text us: (561) 288-0915
              </a>
            </span>
          </p>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-cream-100/10 grid md:grid-cols-2 gap-4 text-xs text-cream-200/55 leading-relaxed">
        <div className="space-y-2">
          <p><span className="text-sage-300 font-medium">For Research Use Only.</span> Not for Human Consumption.</p>
          <p>Not intended to diagnose, treat, cure, or prevent any disease.</p>
          <p>Products are intended exclusively for laboratory and scientific research purposes.</p>
          <p>Not FDA approved for diagnostic or therapeutic use.</p>
        </div>
        <div className="md:text-right">
          <p>© {new Date().getFullYear()} LuviaRx Research Compounds. All rights reserved.</p>
          <p className="mt-1">21+ qualified researchers only.</p>
        </div>
      </div>

      {/* Office address — muted, understated, sits below the legal
          disclaimers so it reads as a compliance detail rather than a
          headline. Kept as a two-line address block for readability. */}
      <div className="mt-6 text-[11px] text-cream-200/40 leading-relaxed text-center md:text-left">
        <p>1489 W. Palmetto Road, Suite 500 #2057</p>
        <p>Boca Raton, FL 33486</p>
      </div>
    </div>
  </footer>
);

export default Footer;
